// utils/PromotionPerformanceMonitor.js
class PromotionPerformanceMonitor {
  constructor() {
    this.metrics = {
      initialLoad: null,
      imageLoadTimes: [],
      cacheHits: 0,
      cacheMisses: 0,
      totalPromotionsLoaded: 0
    };
    this.startTime = null;
  }

  // Start timing initial page load
  startInitialLoad() {
    this.startTime = performance.now();
    console.log('🚀 Starting promotion page load...');
  }

  // End timing initial page load
  endInitialLoad(promotionCount) {
    if (this.startTime) {
      const loadTime = performance.now() - this.startTime;
      this.metrics.initialLoad = loadTime;
      this.metrics.totalPromotionsLoaded = promotionCount;
      
      console.log(`✅ Initial load completed in ${loadTime.toFixed(2)}ms for ${promotionCount} promotions`);
      
      // Log performance warning if too slow
      if (loadTime > 3000) {
        console.warn(`⚠️ Slow initial load: ${loadTime.toFixed(2)}ms (target: <3000ms)`);
      }
      
      this.startTime = null;
    }
  }

  // Track image load time
  trackImageLoad(imageUrl, loadTime) {
    this.metrics.imageLoadTimes.push({
      url: imageUrl,
      loadTime,
      timestamp: Date.now()
    });
    
    // Keep only last 50 image load times
    if (this.metrics.imageLoadTimes.length > 50) {
      this.metrics.imageLoadTimes = this.metrics.imageLoadTimes.slice(-50);
    }
  }

  // Track cache performance
  trackCacheHit() {
    this.metrics.cacheHits++;
    console.log('💾 Cache hit - fast load!');
  }

  trackCacheMiss() {
    this.metrics.cacheMisses++;
    console.log('🌐 Cache miss - fetching from server');
  }

  // Get performance summary
  getPerformanceSummary() {
    const avgImageLoadTime = this.metrics.imageLoadTimes.length > 0
      ? this.metrics.imageLoadTimes.reduce((sum, img) => sum + img.loadTime, 0) / this.metrics.imageLoadTimes.length
      : 0;

    const cacheHitRate = this.metrics.cacheHits + this.metrics.cacheMisses > 0
      ? (this.metrics.cacheHits / (this.metrics.cacheHits + this.metrics.cacheMisses)) * 100
      : 0;

    return {
      initialLoadTime: this.metrics.initialLoad,
      averageImageLoadTime: avgImageLoadTime,
      cacheHitRate: cacheHitRate,
      totalPromotionsLoaded: this.metrics.totalPromotionsLoaded,
      totalImagesLoaded: this.metrics.imageLoadTimes.length
    };
  }

  // Log performance report
  logPerformanceReport() {
    const summary = this.getPerformanceSummary();
    
    console.group('📊 Promotion Performance Report');
    console.log(`Initial Load Time: ${summary.initialLoadTime?.toFixed(2) || 'N/A'}ms`);
    console.log(`Average Image Load: ${summary.averageImageLoadTime.toFixed(2)}ms`);
    console.log(`Cache Hit Rate: ${summary.cacheHitRate.toFixed(1)}%`);
    console.log(`Promotions Loaded: ${summary.totalPromotionsLoaded}`);
    console.log(`Images Loaded: ${summary.totalImagesLoaded}`);
    console.groupEnd();

    // Performance recommendations
    if (summary.initialLoadTime > 3000) {
      console.warn('💡 Consider reducing initial promotion count or optimizing images');
    }
    if (summary.averageImageLoadTime > 1000) {
      console.warn('💡 Consider image compression or CDN optimization');
    }
    if (summary.cacheHitRate < 50) {
      console.warn('💡 Consider increasing cache duration or improving cache strategy');
    }
  }

  // Reset metrics
  reset() {
    this.metrics = {
      initialLoad: null,
      imageLoadTimes: [],
      cacheHits: 0,
      cacheMisses: 0,
      totalPromotionsLoaded: 0
    };
    this.startTime = null;
  }
}

// Create singleton instance
const promotionPerformanceMonitor = new PromotionPerformanceMonitor();

export default promotionPerformanceMonitor;